import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Bell, Sparkles } from "lucide-react";
import { CoursePhaseConfig, getStatusBadgeColor } from "@/lib/curriculumPhases";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface LockedCourseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseName: string;
  config: CoursePhaseConfig;
  phase: number;
}

const LockedCourseModal = ({ open, onOpenChange, courseName, config, phase }: LockedCourseModalProps) => {
  const { user } = useAuth();

  const handleNotifyMe = async () => {
    // Track interest analytics
    if (user) {
      await supabase.from("locked_course_interest").insert({
        user_id: user.id,
        course_name: courseName,
        action: "notify_me",
      }).then(() => {});
    }
    toast.success("You'll be notified when this course launches!", {
      description: `We'll let you know as soon as ${courseName} is available.`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-border/60 bg-card">
        <DialogHeader className="text-left">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Lock className="w-5 h-5 text-primary" />
            </div>
            <Badge className={`${getStatusBadgeColor(config.status)} text-[10px] uppercase tracking-wider font-bold border`}>
              {config.badge}
            </Badge>
          </div>
          <DialogTitle className="font-heading text-lg text-foreground">{courseName}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground leading-relaxed mt-1">
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="rounded-xl border border-border/50 bg-muted/30 p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-foreground">Part of our expansion roadmap</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  This learning path is being released as part of our curated Phase {phase} curriculum rollout. 
                  Founding members will gain access as HeirWise expands.
                </p>
              </div>
            </div>
          </div>

          <Button onClick={handleNotifyMe} className="w-full gap-2" size="lg">
            <Bell className="w-4 h-4" />
            Notify Me When Available
          </Button>

          <p className="text-[10px] text-center text-muted-foreground">
            Continue building mastery with Financial Foundations while new paths are prepared.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LockedCourseModal;
